import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CreditCard, Copy, CheckCircle2, Rocket, Lock, Bitcoin } from "lucide-react";

export default function DemoPayment({ website, onPaid }) {
  const [oxy, setOxy] = useState(null);
  const [open, setOpen] = useState(false);
  const [bd, setBd] = useState({ method: "bkash", transaction_id: "", phone: "" });
  const [crypto, setCrypto] = useState({ network: "", tx_hash: "", from_address: "" });
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    api.get("/oxy/settings").then(r => {
      setOxy(r.data);
      const wallets = (r.data.crypto_wallets || []).filter(w => w.address);
      if (wallets[0]) setCrypto(c => ({ ...c, network: wallets[0].network }));
    });
  }, []);
  useEffect(() => {
    // Return from Stripe success URL?
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (sid && !website.demo_paid) {
      setPolling(true);
      const t = setInterval(async () => {
        try {
          const r = await api.get(`/payments/status/${sid}`);
          if (r.data.payment_status === "paid") {
            clearInterval(t); setPolling(false);
            toast.success("Demo unlocked!");
            onPaid?.();
          }
        } catch {}
      }, 2000);
      setTimeout(() => { clearInterval(t); setPolling(false); }, 60000);
    }
  }, [website.demo_paid]);

  if (website.demo_paid) {
    return (
      <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold px-3 py-1">
        <CheckCircle2 className="w-3.5 h-3.5"/> Demo Unlocked
      </div>
    );
  }

  const stripeCheckout = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/stripe/checkout", { website_id: website.website_id, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Stripe failed"); setBusy(false); }
  };

  const submitBd = async () => {
    if (!bd.transaction_id || !bd.phone) return toast.error("Fill all fields");
    setBusy(true);
    try {
      await api.post("/payments/bd/submit", { website_id: website.website_id, ...bd });
      toast.success("Submitted! Admin will approve shortly");
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const submitCrypto = async () => {
    if (!crypto.network || !crypto.tx_hash) return toast.error("Choose network and enter TxID");
    setBusy(true);
    try {
      await api.post("/payments/crypto/submit", { website_id: website.website_id, ...crypto });
      toast.success("Submitted! Admin will verify shortly");
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-orange-200" data-testid="unlock-demo-btn">
          <Lock className="w-4 h-4 mr-1.5"/>Unlock Demo — ৳{oxy?.demo_fee_bd || 19}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Rocket className="w-5 h-5 text-orange-500"/>Unlock Live Demo</DialogTitle>
        </DialogHeader>
        {polling && (
          <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg text-sm text-sky-700">
            <div className="animate-pulse">Confirming your Stripe payment…</div>
          </div>
        )}
        <Tabs defaultValue="bd">
          <TabsList className="grid grid-cols-3">
            <TabsTrigger value="bd" data-testid="pay-bd-tab">🇧🇩 ৳{oxy?.demo_fee_bd || 19}</TabsTrigger>
            <TabsTrigger value="intl" data-testid="pay-intl-tab">💳 ${oxy?.demo_fee_intl || 1}</TabsTrigger>
            <TabsTrigger value="crypto" data-testid="pay-crypto-tab">₿ Crypto</TabsTrigger>
          </TabsList>

          <TabsContent value="bd" className="space-y-3 mt-3">
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setBd({...bd, method: "bkash"})} className={`p-3 rounded-lg border-2 text-left ${bd.method === "bkash" ? "border-pink-500 bg-pink-50" : "border-slate-200"}`} data-testid="bkash-select">
                <div className="font-bold text-pink-600">bKash <span className="text-[10px] font-normal text-pink-500 uppercase">Personal</span></div>
                <div className="text-xs text-slate-500">Send Money</div>
              </button>
              <button onClick={() => setBd({...bd, method: "nagad"})} className={`p-3 rounded-lg border-2 text-left ${bd.method === "nagad" ? "border-orange-500 bg-orange-50" : "border-slate-200"}`} data-testid="nagad-select">
                <div className="font-bold text-orange-600">Nagad <span className="text-[10px] font-normal text-orange-500 uppercase">Personal</span></div>
                <div className="text-xs text-slate-500">Send Money</div>
              </button>
            </div>
            {oxy && (
              <div className="p-3 bg-slate-50 rounded-lg border text-sm">
                <div className="text-xs text-slate-500 font-bn">নিচের <b>পার্সোনাল</b> নম্বরে <span className="font-mono">৳{oxy.demo_fee_bd}</span> Send Money করুন</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="font-mono font-bold text-lg">{bd.method === "bkash" ? oxy.bkash_number || oxy.whatsapp_number || "01888496974" : oxy.nagad_number || oxy.whatsapp_number || "01888496974"}</span>
                  <button onClick={() => copy(bd.method === "bkash" ? (oxy.bkash_number || oxy.whatsapp_number) : (oxy.nagad_number || oxy.whatsapp_number))} className="text-sky-600"><Copy className="w-4 h-4"/></button>
                </div>
                <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 font-bn">
                  ℹ Send Money সম্পন্ন করে TxID জমা দিন। Admin ম্যানুয়ালি approve করবে (সাধারণত ১-৩০ মিনিট)।
                </div>
              </div>
            )}
            <div><Label>Your phone number</Label><Input value={bd.phone} onChange={e => setBd({...bd, phone: e.target.value})} placeholder="01xxxxxxxxx" data-testid="bd-phone"/></div>
            <div><Label>Transaction ID</Label><Input value={bd.transaction_id} onChange={e => setBd({...bd, transaction_id: e.target.value})} placeholder="8N7A2XYZ" data-testid="bd-txid"/></div>
            <Button onClick={submitBd} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="bd-submit">
              {busy ? "Submitting…" : "Submit for Admin Approval"}
            </Button>
          </TabsContent>

          <TabsContent value="intl" className="space-y-3 mt-3">
            <div className="p-4 bg-slate-50 rounded-lg border">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-slate-700"/>
                <div>
                  <div className="font-bold">Pay with Stripe</div>
                  <div className="text-xs text-slate-500">Cards accepted. Test card: 4242 4242 4242 4242</div>
                </div>
              </div>
              <div className="mt-3 text-3xl font-black font-mono">${oxy?.demo_fee_intl || 1} <span className="text-sm text-slate-400">USD</span></div>
            </div>
            <Button onClick={stripeCheckout} disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="stripe-checkout-btn">
              {busy ? "Redirecting…" : "Pay with Stripe →"}
            </Button>
          </TabsContent>

          <TabsContent value="crypto" className="space-y-3 mt-3">
            {(() => {
              const wallets = (oxy?.crypto_wallets || []).filter(w => w.address);
              if (wallets.length === 0) return (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  Crypto payments are not configured yet. Ask admin to add wallet addresses.
                </div>
              );
              const active = wallets.find(w => w.network === crypto.network) || wallets[0];
              return (
                <>
                  <div className="flex flex-wrap gap-2">
                    {wallets.map(w => (
                      <button key={w.network} onClick={() => setCrypto({...crypto, network: w.network})} className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 ${crypto.network === w.network ? "border-amber-500 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-600"}`} data-testid={`crypto-net-${w.network}`}>
                        {w.network}
                      </button>
                    ))}
                  </div>
                  <div className="p-3 bg-slate-900 text-white rounded-lg">
                    <div className="text-xs opacity-70 uppercase font-bold">Send to this address</div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="font-mono text-xs break-all flex-1">{active.address}</span>
                      <button onClick={() => copy(active.address)} className="text-amber-400"><Copy className="w-4 h-4"/></button>
                    </div>
                    <div className="text-[10px] opacity-60 mt-1">{active.note}</div>
                    <div className="mt-2 text-xs bg-amber-500 text-slate-900 rounded px-2 py-1 inline-block font-bold">Amount: ~${oxy?.demo_fee_intl || 1} USD equivalent</div>
                  </div>
                  <div><Label>Transaction Hash / TxID</Label><Input value={crypto.tx_hash} onChange={e => setCrypto({...crypto, tx_hash: e.target.value})} placeholder="0x... or Tron hash" data-testid="crypto-txhash"/></div>
                  <div><Label>Your wallet address (optional)</Label><Input value={crypto.from_address} onChange={e => setCrypto({...crypto, from_address: e.target.value})}/></div>
                  <Button onClick={submitCrypto} disabled={busy} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold" data-testid="crypto-submit">
                    {busy ? "Submitting…" : "Submit for Admin Verification"}
                  </Button>
                  <div className="text-[11px] text-slate-500">Admin verifies within 30 min. Once approved, demo unlocks automatically.</div>
                </>
              );
            })()}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
